/**
 * Sticky Table Headers for WordPress Table Blocks (ES Module)
 * - Clones each table's <thead> and pins it while the page scroll is within that table
 * - Appends the clone INSIDE the same <figure> so theme styles still apply
 * - Anti-flicker: hysteresis near the bottom + smooth fade-out
 *
 * Usage:
 *   import initStickyTableHeaders from "./stickyTables.js";
 *   const destroy = initStickyTableHeaders({
 *     headerSelector: ".site-header" // your sticky site header element(s)
 *   });
 *   // later: destroy();
 */

export default function initStickyTableHeaders(options = {}) {
    const {
        tableSelector = "figure.wp-block-table",
        headerSelector = ".sticky-header, .site-header, header.wp-block-template-part, header[aria-label='Site']",
        zIndex = 1000,
        fadePx = 12,             // fade window (px) before release at table bottom
        bottomBufferPx = null,   // if null, will use 2 * devicePixelRatio
    } = options;

    const DPR = Math.max(1, Math.floor(window.devicePixelRatio || 1));
    const BOTTOM_BUFFER = bottomBufferPx != null ? bottomBufferPx : 2 * DPR;

    const holders = new Set();           // track created holders for teardown
    const listeners = [];                // [node, event, handler, opts]
    const resizeObservers = [];

    const on = (node, event, handler, opts) => {
        node.addEventListener(event, handler, opts);
        listeners.push([node, event, handler]);
    };

    const observeResize = (el, cb) => {
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(cb);
            ro.observe(el);
            resizeObservers.push(ro);
            return () => ro.disconnect();
        }
        // Fallback: window resize (already added)
        return () => {};
    };

    const getHeaderOffset = () => {
        const h = document.querySelector(headerSelector);
        return h ? h.getBoundingClientRect().height : 0;
    };

    const copyClassesAndAttrs = (srcEl, dstEl) => {
        dstEl.className = srcEl.className;
        for (const { name, value } of Array.from(srcEl.attributes)) {
            if (!/^style$/i.test(name)) dstEl.setAttribute(name, value);
        }
    };

    function setupTable(fig) {
        const table = fig.querySelector("table");
        const thead = table && table.querySelector("thead");
        if (!table || !thead) return;

        const hScrollHost =
            fig.querySelector(".wp-block-table__wrapper") || fig;

        // Fixed header container INSIDE the same figure (keeps theme selectors working)
        const holder = document.createElement("div");
        holder.classList.add("sticky-table-holder");
        Object.assign(holder.style, {
            position: "fixed",
            top: getHeaderOffset() + "px",
            left: "0",
            zIndex: String(zIndex),
            display: "none",
            background: "#fff",
            boxShadow: "0 1px 0 rgba(0,0,0,.06)",
            pointerEvents: "none",
            overflow: "hidden",
            willChange: "transform, opacity",
            transition: "opacity 120ms linear",
        });

        // Shadow table with cloned thead
        const shadowTable = document.createElement("table");
        const shadowHead = thead.cloneNode(true);
        shadowTable.appendChild(shadowHead);

        // Copy classes/attrs so theme CSS applies
        copyClassesAndAttrs(table, shadowTable);
        copyClassesAndAttrs(thead, shadowHead);

        // Copy header cell classes/attrs
        (function copyCellClasses() {
            const src = thead.querySelectorAll("th,td");
            const dst = shadowHead.querySelectorAll("th,td");
            for (let i = 0; i < src.length; i++) {
                if (dst[i]) copyClassesAndAttrs(src[i], dst[i]);
            }
        })();

        holder.appendChild(shadowTable);
        fig.appendChild(holder);
        holders.add(holder);

        // Cache a stable header height to avoid layout jitter during fade
        let holderHeight = thead.getBoundingClientRect().height || 0;
        holder.style.height = holderHeight + "px";

        // Width & column sync
        const syncWidths = () => {
            const rect = table.getBoundingClientRect();
            holder.style.top = getHeaderOffset() + "px";
            holder.style.width = rect.width + "px";

            const src = thead.querySelectorAll("th,td");
            const dst = shadowHead.querySelectorAll("th,td");
            for (let i = 0; i < src.length; i++) {
                const w = src[i].getBoundingClientRect().width;
                if (dst[i]) {
                    Object.assign(dst[i].style, {
                        minWidth: w + "px",
                        maxWidth: w + "px",
                        width: w + "px",
                    });
                }
            }

            // Refresh height if content changed
            const newH = thead.getBoundingClientRect().height || holderHeight;
            if (Math.abs(newH - holderHeight) > 0.5) {
                holderHeight = newH;
                holder.style.height = holderHeight + "px";
            }
        };

        // Keep horizontal scroll aligned
        const syncHorizontal = () => {
            const hostRect = hScrollHost.getBoundingClientRect();
            holder.style.left = hostRect.left + "px";
            holder.style.width = hostRect.width + "px";
            shadowTable.style.transform =
                "translateX(" + -hScrollHost.scrollLeft + "px)";
        };

        // Anti-flicker show/hide with hysteresis + fade near bottom
        let visible = false;

        const onScroll = () => {
            const rect = table.getBoundingClientRect();
            const topOffset = getHeaderOffset();

            const reachedTop = rect.top <= topOffset;
            const bottomDistance = rect.bottom - (topOffset + holderHeight);
            const shouldShow = reachedTop && bottomDistance >= -BOTTOM_BUFFER;

            if (shouldShow !== visible) {
                visible = shouldShow;
                holder.style.display = visible ? "block" : "none";
            }

            if (visible) {
                syncWidths();
                syncHorizontal();

                // Fade out smoothly in the last fadePx before release (bottomDistance 0 → -fadePx)
                const fadeStart = 0;
                const fadeEnd = -Math.max(0, fadePx);
                const t = Math.max(fadeEnd, Math.min(bottomDistance, fadeStart));
                const opacity =
                    fadePx > 0 ? (t - fadeEnd) / (fadeStart - fadeEnd) : 1;
                holder.style.opacity = String(opacity);
            } else {
                holder.style.opacity = "0";
            }
        };

        // Observers & listeners
        const unobserveTable = observeResize(table, () => {
            syncWidths();
            syncHorizontal();
            onScroll();
        });
        const unobserveBody = observeResize(document.body, () => {
            syncWidths();
            syncHorizontal();
            onScroll();
        });

        on(hScrollHost, "scroll", syncHorizontal, { passive: true });
        on(window, "scroll", onScroll, { passive: true });
        on(window, "resize", () => {
            syncWidths();
            syncHorizontal();
            onScroll();
        });

        // Initial sync
        syncWidths();
        syncHorizontal();
        onScroll();

        // return a cleanup just for this table
        return () => {
            unobserveTable();
            unobserveBody();
            if (holder && holder.parentNode) holder.parentNode.removeChild(holder);
            holders.delete(holder);
        };
    }

    // Initialize for all matching tables
    const figures = Array.from(document.querySelectorAll(tableSelector));
    const tableCleanups = figures.map(setupTable).filter(Boolean);

    // Global teardown
    return function destroy() {
        tableCleanups.forEach((fn) => fn && fn());
        listeners.forEach(([node, evt, handler]) =>
            node.removeEventListener(evt, handler)
        );
        listeners.length = 0;
        resizeObservers.forEach((ro) => ro.disconnect());
        resizeObservers.length = 0;
        holders.forEach((h) => h.parentNode && h.parentNode.removeChild(h));
        holders.clear();
    };
}
